import * as THREE from "three"
import { useGLTF, useTexture } from '@react-three/drei'
import { Canvas, useFrame, useThree } from 'react-three-fiber'
import create from "zustand"

import * as Tone from "tone"
import React, { Suspense, useRef, useCallback, useEffect, useMemo, useState } from "react"
import lerp from "lerp"
import { TweenMax, Sine, gsap } from "gsap"
import inst from "./inst"

import { Flock } from "./Flock";

let hitCount = 0
let isMobile = window.innerWidth < 500
let xWidth = isMobile ? 20 : 80

gsap.defaults({
  ease: Sine.easeOut
})
const col = 18
let getInst = () => {
  hitCount++
  return (hitCount % 2 === 0) ? inst.synthL : inst.synthR
}
const [useStore] = create((set, get) => ({
  startTime: null,
  started: false,
  sequence: [],
  parts: [],
  mutation: {
    mouseDown: false,
    pageX: window.innerWidth / 2,
    pageY: window.innerHeight / 2,
    meter: 0,
    timeSpeed: 0,
    interval: '2',
    dir: 0,
    dirTarget: 0,
    locked: false,
    front: 1,
    accent: '8',
    noteLen: 0.5,
    started: false
  },
  locked: false,
  set: (key, val) => set(() => {
    return { [key]: val };
  }),
}))

function Rig() {
  const { camera, mouse } = useThree()
  const vec = new THREE.Vector3()
  const mutation = useStore(state => state.mutation)

  return useFrame(() => {
    camera.position.lerp(vec.set(-mouse.x * 6, -mouse.y * 6, camera.position.z), 0.)
    camera.lookAt(0, 0, 0)
    //if(!mutation.cursor) return

    mutation.meter = lerp(mutation.meter, Math.abs(inst.meterL.getValue() + inst.meterR.getValue()) * (isMobile ? 200 : 400), 0.03)
    mutation.noteLen = lerp(mutation.noteLen, 0.05, mutation.mouseDown ? 0.03 : 0.2)
    let size = (window.innerHeight/8) + mutation.meter * 20
    mutation.cursor.current.style.left = mutation.pageX + 'px'
    mutation.cursor.current.style.top = window.innerHeight - mutation.pageY + 'px'
    mutation.cursor.current.style.width = size + 'px'
    mutation.cursor.current.style.height = size + 'px'
    mutation.inverter1.current.style.left = mutation.pageX+'px'
    mutation.inverter1.current.style.top = (window.innerHeight-mutation.pageY)+'px'
    mutation.inverter2.current.style.left = mutation.pageX+'px'
    mutation.inverter2.current.style.top = (window.innerHeight-mutation.pageY)+'px'
  })
}

function Swarm({ count }) {
  const mutation = useStore(state => state.mutation)

  const mesh = useRef()
  const dummy = useMemo(() => new THREE.Object3D(), [])

  const particles = useMemo(() => {
    const temp = []
    for (let i = 0; i < count; i++) {
      const t = Math.random() * 100
      const factor = 20 + Math.random() * 100
      const speed = 0.01 + Math.random() / 200
      const xFactor = (-xWidth / 2) + Math.random() * xWidth
      const yFactor = -20 + Math.random() * 20
      const zFactor = -30 + Math.random() * 60
      temp.push({ t, factor, speed, xFactor, yFactor, zFactor, mx: 0, my: 0 })
    }
    return temp
  }, [count])

  const mult = 0.08;
  useFrame(state => {
    mutation.timeSpeed = lerp(mutation.timeSpeed, (mutation.mouseDown ? 0.03 : 0), (mutation.mouseDown ? 0.2 : 0.1))
    mutation.dir = lerp(mutation.dir, mutation.dirTarget, 0.002)
    particles.forEach((particle, i) => {
      let { t, factor, speed, xFactor, yFactor, zFactor } = particle
      t = particle.t += ((speed / 110) + mutation.timeSpeed * (i / count)) * mutation.dir
      const a = Math.cos(t) + Math.sin(t * 1) / 10
      const b = Math.sin(t) + Math.cos(t * 2) / 10
      const s = 0.2 + Math.max(0.25, Math.cos(t) * 1.3) * mutation.meter
      particle.mx += ((mutation.pageX / window.innerWidth * (window.innerWidth * mult)) - particle.mx) * 0.01
      particle.my += (mutation.pageY / window.innerHeight * (window.innerHeight * mult) - particle.my) * 0.02
      dummy.position.set(
        (particle.mx / 10) * a + xFactor + Math.cos((t / 10) * factor) + (Math.sin(t * 1) * factor) / 10,
        10 + (particle.my / 10) * mutation.front * b + yFactor + Math.sin((t / 10) * factor) + (Math.cos(t * 2) * factor) / 200,
        50 + (-50 * (mutation.front)) + (particle.my / 10) * b + zFactor + Math.cos((t / 10) * factor) + (Math.sin(t * 3) * factor) / 10
      )
      dummy.scale.set(s, s, s)
      dummy.updateMatrix()
      mesh.current.setMatrixAt(i, dummy.matrix)
    })
    mesh.current.instanceMatrix.needsUpdate = true
  })

  return (
    <>
      <instancedMesh ref={mesh} args={[null, null, count]}>
        <sphereBufferGeometry attach="geometry" args={[0.1, 16, 16]} />
        <meshBasicMaterial attach="material" color="white"
        />
      </instancedMesh>
    </>
  )
}

function Magnet({ mouse }) {
  const { size, viewport } = useThree();
  const light = useRef();
  const aspect = size.width / viewport.width;

  const [active, setActive] = useState(0)

  useFrame(state => {
    let mousePosition = new THREE.Vector3(
      mouse.current[0] / aspect,
      -mouse.current[1] / aspect,
      0
    );
    let scale = THREE.MathUtils.lerp(
      light.current.scale.x,
      mouse.current[2] ? 10 : 1,
      0.1
    )
    light.current.position.set(
      mousePosition.x,
      mousePosition.y,
      mousePosition.z
    );
    light.current.scale.set(scale, scale, scale)
  });
  return (
    <>
      <ambientLight intensity={0.5} color={"white"} />
      <directionalLight intensity={1} color={"white"} />
      <pointLight ref={light} distance={125} intensity={3} color="white">
      </pointLight>
    </>
  );
}

export default function () {
  const mutation = useStore(state => state.mutation)
  const set = useStore(state => state.set)
  const locked = useStore(state => state.locked)
  const inverter1 = useRef()
  const inverter2 = useRef()
  const inverter3 = useRef()
  const cursor = useRef()
  const timeout = useRef()
  const randomizer = useRef()
  const inverted = useRef(false)

  const mouse = useRef([0, 0, false]);

  const onMouseMove = useCallback((e) => {
    mouse.current = [e.clientX - window.innerWidth / 2, e.clientY - window.innerHeight / 2, mouse.current[2]];
    if (e.buttons === 0) {
      mutation.mouseDown = false
    }
    mutation.pageX = e.pageX
    mutation.pageY = window.innerHeight - e.pageY
  },
    []
  );

  const handleMouseDown = event => {
    if (event.button !== 2) {
      mouse.current[2] = true;
    }
    play(2)
    document.body.style.cursor = "none";
    mutation.mouseDown = true
    mutation.started = true
    randomize()
    clearTimeout(timeout.current)
    clearInterval(randomizer.current)
    randomizer.current = setInterval(() => {
      randomize()
    }, 60000)
  };

  const handleMouseUp = event => {
    if (event.button !== 2) {
      mouse.current[2] = false;
    }
    mutation.mouseDown = false
  };

  useEffect(() => {

    setInterval(() => {
      if (!mutation.started || mutation.interval.indexOf('1') === -1) return
      play(2.5)
    }, 400)
    setInterval(() => {
      if (!mutation.started || mutation.interval.indexOf('2') === -1) return
      play(3)
    }, 600)
    setInterval(() => {
      if (!mutation.started || mutation.interval.indexOf('3') === -1) return
      play(4)
    }, 740)
    setInterval(() => {
      if (!mutation.started || mutation.interval.indexOf('4') === -1) return
      play(2.5)
    }, 500)
    setInterval(() => {
      if (!mutation.started || mutation.interval.indexOf('5') === -1) return
      play(3.4)
    }, 700)
    setInterval(() => {
      if (!mutation.started || mutation.interval.indexOf('6') === -1) return
      play(3.4)
    }, 450)
    setInterval(() => {
      if (!mutation.started || mutation.interval.indexOf('7') === -1) return
      play(4)
    }, 2000)

    setInterval(() => {
      if(mouse.current[2]) return
      mouse.current[2] = true
      timeout.current = setTimeout(() => {
        mouse.current[2] = false
      },600)
    }, 2000)

    mutation.cursor = cursor
    mutation.inverter1 = inverter1
    mutation.inverter2 = inverter2
  }, [])

  const play = useCallback((vel) => {
    let s = getInst()
    let hit = 0
    let punch = 0
    mutation.meter = mutation.meter + vel*0.2
    mutation.noteLen = Math.min((mutation.noteLen + (vel)),3)
    for (let i = 0; i < mutation.accent.length; i++) {
      if (hitCount % mutation.accent[i] === 0) {
        hit = 1200 / (mutation.accent[i])
        punch = 0.2
        //break;
      }
    }
    let freq = 30 + (isMobile ? (mutation.pageX * 1.75) : (mutation.pageY * 1.2)) + hit
    s.triggerAttackRelease(freq*0.3, mutation.noteLen, '+0.01', vel + (Math.random() * 0.2) + punch);
  }, [])
  const newAccent = useCallback(() => {
    mutation.accent = ''
      new Array(9).fill().forEach((v, i) => Math.random() > 0.9 ? mutation.accent += (i + 1) : null)
  }, [])
  let ramps = ['linearRampTo', 'exponentialRampTo']
  const randomize = useCallback((vel) => {
    if (mutation.locked) return
    setTimeout(() => {
      
      mutation.interval = ''
      new Array(7).fill().forEach((v, i) => Math.random() > 0.8 ? mutation.interval += (i+1) : null)
      if (mutation.interval.length === 0)
      mutation.interval = '1'
    }, 1000)
    mutation.dirTarget = (Math.random() * 3) - 1.5
   
    let inverterSize = isMobile ? '240vh' : '300vw'
    inverter1.current.style.width = inverterSize
    inverter1.current.style.height = inverterSize
    
    inverter2.current.style.width = inverterSize
    inverter2.current.style.height = inverterSize
    if (inverted.current) {
      TweenMax.to('.invert1', 1.2, { delay: 0, width: '0vh', height: '0vh', ease: Sine.easeOut })
      inverter3.current.style.width = '0vh'
      inverter3.current.style.height = '0vh'
    } else {
      TweenMax.to('.invert2', 1.2, { delay: 0, width: '0vh', height: '0vh', ease: Sine.easeOut })
      inverter3.current.style.width = inverterSize
      inverter3.current.style.height = inverterSize
    }
    inverted.current = !inverted.current

    let rampTime = 2

    inst.delayL.wet[ramps[Math.round(Math.random())]](Math.random() * 0.25, rampTime)
    inst.delayL.delayTime[ramps[Math.round(Math.random())]](Math.random() * 8, rampTime*0.5)
    inst.delayL.feedback[ramps[Math.round(Math.random())]](Math.random() * 0.1, rampTime*2)

    inst.delayR.wet[ramps[Math.round(Math.random())]](Math.random() * 0.25, rampTime)
    inst.delayR.delayTime[ramps[Math.round(Math.random())]](Math.random() * 8, rampTime*0.5)
    inst.delayR.feedback[ramps[Math.round(Math.random())]](Math.random() * 0.1, rampTime*2)
  }, [])
  return (
    <>
      <Canvas camera={{ position: [0, 0, 50], fov: 60, far: 500 }}
        /*onPointerMove={mouseMove}
        onMouseMove={mouseMove}
        onMouseUp={mouseUp}

        onPointerDown={mouseDown}
        onPointerUp={mouseUp}
        */
        onPointerDown={(e) => isMobile && handleMouseDown(e)}
        onPointerMove={onMouseMove}
        onPointerUp={handleMouseUp}
        onMouseMove={onMouseMove}
        onMouseDown={(e) => !isMobile && handleMouseDown(e)}
        onMouseUp={handleMouseUp}
        gl={{ antialias: true }}
        //pixelRatio={0.4 * window.devicePixelRatio}
        onCreated={({ gl }) => {
          gl.toneMapping = THREE.ACESFilmicToneMapping
          gl.outputEncoding = THREE.sRGBEncoding
        }}
      >
        <ambientLight intensity={0.4} />
        <spotLight
          position={[10, 10, 10]}
          angle={0.2}
          penumbra={1}
          intensity={0.2}
        />
        <Rig />
        <directionalLight position={[-1, 4, -2]} intensity={2} color="white" />
        <pointLight position={[-1, -4, -0]} intensity={3} color="white" />
        <Flock mouse={mouse} count={isMobile ? 400 : 500} />
        <Magnet mouse={mouse} />
      </Canvas>
      <div ref={inverter1} className='invert1' />
      <div ref={inverter2} className='invert2' />
      <div ref={inverter3} className='invert3' />
      <div ref={cursor} className="cursor"/>
    </>
  )
}

console.log('%c🌱🌱 @ramioso 2021-05-25 🌱🌱 ', 'color: orange');